import { Head, router } from '@inertiajs/react'
import { FormEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import {
  HiOutlineInformationCircle,
  HiOutlinePencilSquare,
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineXMark,
} from 'react-icons/hi2'
import { HiXMark } from 'react-icons/hi2'
import ConfirmDialog from '../../components/ConfirmDialog'
import EventLayout from '../../components/EventLayout'
import IconPicker from '../../components/IconPicker'
import InfoDialog from '../../components/InfoDialog'
import ScoringExplainer from '../../components/ScoringExplainer'
import SortableList from '../../components/SortableList'
import { resolveIcon } from '../../components/LeadForm/iconResolver'

// ────────────────────────────────────────────────────────────────
// Types (shape matches EventsController#setup props)
// ────────────────────────────────────────────────────────────────

type QuestionType = 'single' | 'multi'
type StepType = 'choice' | 'contact'
type FieldType = 'text' | 'email' | 'tel' | 'number' | 'url'

type ServerOption = {
  id: number
  label: string
  description: string
  icon: string
  score: number
  lead_count: number
}

type ServerContactField = {
  id: number
  key: string
  label: string
  field_type: FieldType
  required: boolean
  system: boolean
  locked_key: boolean
  locked_field_type: boolean
  locked_required: boolean
  lead_count: number
}

type ServerStep = {
  id: number
  step_type: StepType
  text: string
  description: string
  question_type: QuestionType | null
  position: number
  question_template_id: number | null
  options: ServerOption[]
  contact_fields: ServerContactField[]
}

type ServerTemplate = {
  id: number
  text: string
  description: string
  question_type: QuestionType

  options: Array<{ label: string; description: string; icon: string; score: number }>
}

type EventPayload = {
  id: number
  title: string
  slug: string
  location: string
  date_range: string
  status: 'upcoming' | 'ongoing' | 'completed'
  banner_url: string | null
}

type Props = {
  event: EventPayload
  events: Array<{ title: string; slug: string }>
  steps: ServerStep[]
  templates: ServerTemplate[]
}

// ────────────────────────────────────────────────────────────────
// Client-side state
// ────────────────────────────────────────────────────────────────

type OptionState = {
  id: string
  db_id: number | null
  label: string
  description: string
  icon: string
  score: number
  lead_count: number
}

type ContactFieldState = {
  id: string
  db_id: number | null
  key: string
  label: string
  field_type: FieldType
  required: boolean
  system: boolean
  locked_key: boolean
  locked_field_type: boolean
  locked_required: boolean
  lead_count: number
}

type StepState = {
  id: string
  db_id: number | null
  step_type: StepType
  text: string
  description: string
  question_type: QuestionType

  options: OptionState[]
  contact_fields: ContactFieldState[]
  from_template_id: number | null
}

let tempCounter = 0
const nextTempId = () => `new-${++tempCounter}`

const toStepState = (s: ServerStep): StepState => ({
  id: `step-${s.id}`,
  db_id: s.id,
  step_type: s.step_type,
  text: s.text,
  description: s.description,
  question_type: (s.question_type || 'single') as QuestionType,
  options: s.options.map((o) => ({
    id: `opt-${o.id}`,
    db_id: o.id,
    label: o.label,
    description: o.description,
    icon: o.icon,
    score: o.score,
    lead_count: o.lead_count,
  })),
  contact_fields: s.contact_fields.map((f) => ({
    id: `cf-${f.id}`,
    db_id: f.id,
    key: f.key,
    label: f.label,
    field_type: f.field_type,
    required: f.required,
    system: f.system,
    locked_key: f.locked_key,
    locked_field_type: f.locked_field_type,
    locked_required: f.locked_required,
    lead_count: f.lead_count,
  })),
  from_template_id: null,
})

// ────────────────────────────────────────────────────────────────
// Destructive-edit detection
// ────────────────────────────────────────────────────────────────

type DestructiveImpact = {
  deleted_steps: Array<{ text: string; lead_count: number }>
  deleted_options: Array<{ step_text: string; label: string; lead_count: number }>
  deleted_contact_fields: Array<{ label: string; lead_count: number }>
  total_leads_affected: number
}

function detectImpact(original: ServerStep[], current: StepState[]): DestructiveImpact {
  const currentStepDbIds = new Set(current.map((s) => s.db_id).filter((v): v is number => v !== null))
  const currentOptionDbIds = new Set<number>()
  const currentFieldDbIds = new Set<number>()
  current.forEach((s) => {
    s.options.forEach((o) => o.db_id && currentOptionDbIds.add(o.db_id))
    s.contact_fields.forEach((f) => f.db_id && currentFieldDbIds.add(f.db_id))
  })

  const impact: DestructiveImpact = {
    deleted_steps: [],
    deleted_options: [],
    deleted_contact_fields: [],
    total_leads_affected: 0,
  }

  original.forEach((s) => {
    if (!currentStepDbIds.has(s.id) && s.step_type === 'choice') {
      const count = s.options.reduce((acc, o) => acc + o.lead_count, 0)
      impact.deleted_steps.push({ text: s.text, lead_count: count })
      impact.total_leads_affected += count
      return
    }
    s.options.forEach((o) => {
      if (!currentOptionDbIds.has(o.id) && o.lead_count > 0) {
        impact.deleted_options.push({ step_text: s.text, label: o.label, lead_count: o.lead_count })
        impact.total_leads_affected += o.lead_count
      }
    })
    s.contact_fields.forEach((f) => {
      if (!currentFieldDbIds.has(f.id) && f.lead_count > 0) {
        impact.deleted_contact_fields.push({ label: f.label, lead_count: f.lead_count })
        impact.total_leads_affected += f.lead_count
      }
    })
  })

  return impact
}

function isDestructive(i: DestructiveImpact): boolean {
  return (
    i.deleted_steps.length > 0 ||
    i.deleted_options.length > 0 ||
    i.deleted_contact_fields.length > 0
  )
}

// ────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────

export default function FormSetup({ event, events, steps: serverSteps, templates }: Props) {
  const [steps, setSteps] = useState<StepState[]>(() => serverSteps.map(toStepState))
  const [processing, setProcessing] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [scoringInfoOpen, setScoringInfoOpen] = useState(false)
  const [pendingImpact, setPendingImpact] = useState<DestructiveImpact | null>(null)
  const [editingStepId, setEditingStepId] = useState<string | null>(null)

  // Ref map for scrolling to newly added steps
  const stepRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const pendingScrollId = useRef<string | null>(null)

  const editingStep = editingStepId ? steps.find((s) => s.id === editingStepId) ?? null : null

  const updateStep = (id: string, patch: Partial<StepState>) =>
    setSteps((list) => list.map((s) => (s.id === id ? { ...s, ...patch } : s)))

  const removeStep = (id: string) => {
    setSteps((list) => list.filter((s) => s.id !== id))
    if (editingStepId === id) setEditingStepId(null)
  }

  const addBlankChoiceStep = () => {
    const newId = nextTempId()
    const newStep: StepState = {
      id: newId,
      db_id: null,
      step_type: 'choice',
      text: 'New question',
      description: '',
      question_type: 'single',
      options: [
        { id: nextTempId(), db_id: null, label: 'Option 1', description: '', icon: '', score: 0, lead_count: 0 },
      ],
      contact_fields: [],
      from_template_id: null,
    }
    setSteps((list) => [...list, newStep])
    pendingScrollId.current = newId
    setEditingStepId(newId)
  }

  const addFromTemplate = (tpl: ServerTemplate) => {
    const newId = nextTempId()
    const newStep: StepState = {
      id: newId,
      db_id: null,
      step_type: 'choice',
      text: tpl.text,
      description: tpl.description,
      question_type: tpl.question_type,
      options: tpl.options.map((o) => ({
        id: nextTempId(),
        db_id: null,
        label: o.label,
        description: o.description,
        icon: o.icon,
        score: o.score,
        lead_count: 0,
      })),
      contact_fields: [],
      from_template_id: tpl.id,
    }
    setSteps((list) => [...list, newStep])
    setPickerOpen(false)
    pendingScrollId.current = newId
    setEditingStepId(newId)
  }

  // Scroll to newly added step after render
  useEffect(() => {
    if (!pendingScrollId.current) return
    const id = pendingScrollId.current
    pendingScrollId.current = null
    // Small delay to let DOM settle after state update
    requestAnimationFrame(() => {
      const el = stepRefs.current.get(id)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
  }, [steps])

  const submit = () => {
    setProcessing(true)
    const payload = {
      setup: {
        steps: steps.map((s) => ({
          id: s.db_id,
          step_type: s.step_type,
          text: s.text,
          description: s.description,
          question_type: s.question_type,
          from_template_id: s.from_template_id,
          options: s.options.map((o) => ({
            id: o.db_id,
            label: o.label,
            description: o.description,
            icon: o.icon,
            score: o.score,
          })),
          contact_fields: s.contact_fields.map((f) => ({
            id: f.db_id,
            key: f.key,
            label: f.label,
            field_type: f.field_type,
            required: f.required,
          })),
        })),
      },
    }
    router.patch(`/events/${event.slug}/form-setup`, payload, {
      onFinish: () => {
        setProcessing(false)
        setPendingImpact(null)
        setEditingStepId(null)
      },
    })
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const impact = detectImpact(serverSteps, steps)
    if (isDestructive(impact)) {
      setPendingImpact(impact)
    } else {
      submit()
    }
  }

  return (
    <>
      <Head title={`${event.title} — Setup`} />
      <EventLayout event={event} events={events} active="setup">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="font-display text-2xl font-bold text-ink">Form Setup</h2>
              <p className="mt-1 font-display text-sm text-black/60">
                Drag to reorder steps. Click Edit to modify a step.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setScoringInfoOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2.5 font-display text-sm font-medium text-ink transition hover:bg-gray-50"
              >
                <HiOutlineInformationCircle className="h-4 w-4" />
                How scoring works
              </button>
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2.5 font-display text-sm font-medium text-ink transition hover:bg-gray-50"
              >
                <HiOutlinePlus className="h-4 w-4" />
                Add from template
              </button>
              <button
                type="button"
                onClick={addBlankChoiceStep}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2.5 font-display text-sm font-medium text-ink transition hover:bg-gray-50"
              >
                <HiOutlinePlus className="h-4 w-4" />
                Add blank step
              </button>
            </div>
          </div>

          <SortableList
            items={steps}
            onReorder={setSteps}
            layout="grid"
            gridClassName="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
            renderItem={(step, { dragHandle }) => {
              const stepIndex = steps.findIndex((s) => s.id === step.id)
              return (
                <div ref={(el) => { if (el) stepRefs.current.set(step.id, el); else stepRefs.current.delete(step.id) }}>
                  <StepSummaryCard
                    step={step}
                    stepNumber={stepIndex + 1}
                    dragHandle={dragHandle}
                    isEditing={editingStepId === step.id}
                    onEdit={() => setEditingStepId(step.id)}
                    onRemove={() => removeStep(step.id)}
                  />
                </div>
              )
            }}
          />

          <div className="flex items-center justify-end gap-2">
            <a
              href={`/events/${event.slug}/analytics`}
              className="rounded-md border border-gray-300 bg-white px-5 py-3 font-display text-base font-medium text-ink transition hover:bg-gray-50"
            >
              Cancel
            </a>
            <button
              type="submit"
              disabled={processing}
              className="rounded-md bg-ink px-7 py-3 font-display text-base font-medium text-white shadow-sm transition hover:bg-midnight-green disabled:cursor-not-allowed disabled:opacity-60"
            >
              {processing ? 'Saving…' : 'Save Setup'}
            </button>
          </div>
        </form>
      </EventLayout>

      {/* Edit drawer */}
      <StepDrawer
        open={editingStep !== null}
        step={editingStep}
        onUpdate={(patch) => editingStepId && updateStep(editingStepId, patch)}
        onRemove={() => editingStepId && removeStep(editingStepId)}
        onClose={() => setEditingStepId(null)}
      />

      {/* Template picker */}
      {pickerOpen && (
        <TemplatePickerDialog
          templates={templates}
          onPick={addFromTemplate}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {/* Destructive-edit confirmation */}
      <ConfirmDialog
        open={pendingImpact !== null}
        tone="danger"
        title="This will affect existing leads"
        body={pendingImpact ? <ImpactSummary impact={pendingImpact} /> : null}
        confirmLabel="Save anyway"
        onConfirm={submit}
        onClose={() => setPendingImpact(null)}
      />

      {/* Scoring info */}
      <InfoDialog size="lg" open={scoringInfoOpen} title="How lead scoring works" onClose={() => setScoringInfoOpen(false)}>
        <ScoringExplainer showAiSection={false} />
      </InfoDialog>
    </>
  )
}

// ────────────────────────────────────────────────────────────────
// StepSummaryCard — compact read-only view of a step.
// Shows: type badge, question text, description, options summary,
// question type, scoring mode. Click "Edit" to open drawer.
// ────────────────────────────────────────────────────────────────

function StepSummaryCard({
  step,
  stepNumber,
  dragHandle,
  isEditing,
  onEdit,
  onRemove,
}: {
  step: StepState
  stepNumber: number
  dragHandle: ReactNode
  isEditing: boolean
  onEdit: () => void
  onRemove: () => void
}) {
  const isContact = step.step_type === 'contact'
  const totalLeads = isContact
    ? step.contact_fields.reduce((sum, f) => sum + f.lead_count, 0)
    : step.options.reduce((sum, o) => sum + o.lead_count, 0)

  return (
    <div
      className={`flex h-full flex-col rounded-xl bg-white shadow-sm transition-shadow ${
        isEditing ? 'ring-2 ring-midnight-green/40' : ''
      }`}
    >
      {/* Card header — number, drag handle, badges, actions */}
      <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ink font-display text-xs font-bold text-white">
          {stepNumber}
        </span>
        {dragHandle}
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 font-display text-[11px] font-medium ${
            isContact
              ? 'bg-purple-100 text-purple-700'
              : step.question_type === 'single'
                ? 'bg-[#93D500]/20 text-midnight-green'
                : 'bg-[#00BED6]/20 text-midnight-green'
          }`}
        >
          {isContact ? 'Contact' : step.question_type === 'single' ? 'Single Choice' : 'Multi Choice'}
        </span>
        <div className="flex-1" />
        <button
          type="button"
          onClick={onEdit}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-black/40 transition hover:bg-gray-100 hover:text-midnight-green"
          aria-label="Edit step"
        >
          <HiOutlinePencilSquare className="h-4 w-4" />
        </button>
        {!isContact && (
          <button
            type="button"
            onClick={onRemove}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-black/20 transition hover:bg-rose-50 hover:text-rose-600"
            aria-label="Delete step"
          >
            <HiOutlineTrash className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Card body */}
      <div className="flex-1 px-3.5 py-3">
        <h3 className="font-display text-sm font-semibold text-ink leading-snug line-clamp-2">
          {step.text || <span className="text-black/30 italic">Untitled</span>}
        </h3>
        {step.description && (
          <p className="mt-0.5 font-display text-xs text-black/45 line-clamp-2">
            {step.description}
          </p>
        )}

        {isContact ? (
          <ContactFieldsSummary fields={step.contact_fields} />
        ) : (
          <OptionsSummary options={step.options} />
        )}
      </div>

      {/* Card footer — lead count */}
      <div className="border-t border-gray-100 px-3.5 py-2">
        <span className="font-display text-xs text-black/40">
          {totalLeads} lead{totalLeads === 1 ? '' : 's'}
        </span>
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────
// OptionsSummary — compact view of choice options
// ────────────────────────────────────────────────────────────────

function OptionsSummary({ options }: { options: OptionState[] }) {
  if (options.length === 0) return null

  return (
    <div className="mt-2.5 flex flex-col gap-1">
      {options.map((opt, i) => (
        <div
          key={opt.id}
          className="flex items-center gap-1.5 rounded bg-gray-50 px-2 py-1"
        >
          {/* Icon or number */}
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-white text-[10px] text-black/40 shadow-sm">
            {resolveIcon(opt.icon, 'h-3 w-3 text-midnight-green') || (
              <span className="font-display font-medium">{i + 1}</span>
            )}
          </span>
          {/* Label */}
          <span className="min-w-0 flex-1 truncate font-display text-xs font-medium text-ink">
            {opt.label || <span className="text-black/30 italic">—</span>}
          </span>
          {/* Score + leads as labeled pills */}
          {opt.score > 0 && (
            <span className="shrink-0 rounded bg-[#93D500]/15 px-1.5 py-0.5 font-display text-[10px] font-medium tabular-nums text-midnight-green">
              {opt.score} pts
            </span>
          )}
          <span className="shrink-0 font-display text-[10px] tabular-nums text-black/35">
            {opt.lead_count} {opt.lead_count === 1 ? 'lead' : 'leads'}
          </span>
        </div>
      ))}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────
// ContactFieldsSummary — compact view of contact fields
// ────────────────────────────────────────────────────────────────

function ContactFieldsSummary({ fields }: { fields: ContactFieldState[] }) {
  if (fields.length === 0) return null

  return (
    <div className="mt-2.5 flex flex-wrap gap-1">
      {fields.map((f) => (
        <span
          key={f.id}
          className="inline-flex items-center gap-1 rounded bg-gray-50 px-2 py-0.5 font-display text-[11px] text-ink"
        >
          {f.label || f.key}
          {f.required && <span className="text-rose-400">*</span>}
          <span className="text-black/25">{f.field_type}</span>
        </span>
      ))}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────
// StepDrawer — right-side slide-out panel for editing a step.
// Pattern matches FilterDrawer: dark header, scrollable body,
// sticky footer.
// ────────────────────────────────────────────────────────────────

function StepDrawer({
  open,
  step,
  onUpdate,
  onRemove,
  onClose,
}: {
  open: boolean
  step: StepState | null
  onUpdate: (patch: Partial<StepState>) => void
  onRemove: () => void
  onClose: () => void
}) {
  // Close on Escape
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // Lock body scroll
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  const isContact = step?.step_type === 'contact'

  return (
    <div
      className={`fixed inset-0 z-50 ${open ? '' : 'pointer-events-none'}`}
      aria-hidden={!open}
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close editor"
        onClick={onClose}
        className={`absolute inset-0 bg-black/40 transition-opacity ${
          open ? 'opacity-100' : 'opacity-0'
        }`}
      />

      {/* Panel */}
      <aside
        role="dialog"
        aria-label="Edit step"
        className={`absolute right-0 top-0 flex h-full w-full max-w-xl flex-col bg-[#EFEFEF] shadow-xl transition-transform duration-200 ease-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <header className="flex items-center gap-3 bg-ink px-5 py-5">
          <h2 className="flex-1 font-display text-base font-medium text-white">
            {step ? (isContact ? 'Edit Contact Step' : 'Edit Question') : 'Edit Step'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
            aria-label="Close"
          >
            <HiXMark className="h-5 w-5" />
          </button>
        </header>

        {/* Scrollable body */}
        {step && (
          <div className="flex-1 overflow-y-auto px-5 py-5">
            <div className="space-y-5">
              {/* Step type & question type */}
              {!isContact && (
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={step.question_type}
                    onChange={(e) => onUpdate({ question_type: e.target.value as QuestionType })}
                    className="rounded-md border border-gray-300 bg-white py-2 pl-3 pr-8 font-display text-sm text-ink"
                  >
                    <option value="single">Single choice</option>
                    <option value="multi">Multi choice</option>
                  </select>
                </div>
              )}

              {/* Question text */}
              <div>
                <label className="mb-1.5 block font-display text-xs font-medium text-black/50">
                  {isContact ? 'Section title' : 'Question'}
                </label>
                <input
                  type="text"
                  value={step.text}
                  onChange={(e) => onUpdate({ text: e.target.value })}
                  placeholder={isContact ? 'Section title' : 'Question'}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-3 font-display text-base font-medium text-ink shadow-sm focus:border-midnight-green focus:outline-none focus:ring-1 focus:ring-midnight-green"
                />
              </div>

              {/* Description */}
              <div>
                <label className="mb-1.5 block font-display text-xs font-medium text-black/50">
                  Description
                </label>
                <textarea
                  rows={2}
                  value={step.description}
                  onChange={(e) => onUpdate({ description: e.target.value })}
                  placeholder="Description (optional)"
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 font-display text-sm text-ink shadow-sm focus:border-midnight-green focus:outline-none focus:ring-1 focus:ring-midnight-green"
                />
              </div>

              {/* Options or contact fields */}
              {isContact ? (
                <ContactFieldsEditor
                  fields={step.contact_fields}
                  onChange={(next) => onUpdate({ contact_fields: next })}
                />
              ) : (
                <OptionsEditor
                  options={step.options}
                  onChange={(next) => onUpdate({ options: next })}
                />
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="flex items-center justify-between border-t border-gray-200 bg-[#EFEFEF] px-5 py-4">
          <div>
            {step && !isContact && (
              <button
                type="button"
                onClick={() => {
                  onRemove()
                  onClose()
                }}
                className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 font-display text-sm font-medium text-rose-600 transition hover:bg-rose-50"
              >
                <HiOutlineTrash className="h-4 w-4" />
                Delete step
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md bg-ink px-6 py-3 font-display text-base font-medium text-white transition hover:bg-midnight-green"
          >
            Done
          </button>
        </footer>
      </aside>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────
// OptionsEditor — sortable list of choice options (inside drawer)
// ────────────────────────────────────────────────────────────────

function OptionsEditor({
  options,
  onChange,
}: {
  options: OptionState[]
  onChange: (next: OptionState[]) => void
}) {
  const update = (id: string, patch: Partial<OptionState>) =>
    onChange(options.map((o) => (o.id === id ? { ...o, ...patch } : o)))
  const remove = (id: string) => onChange(options.filter((o) => o.id !== id))
  const add = () =>
    onChange([
      ...options,
      { id: nextTempId(), db_id: null, label: '', description: '', icon: '', score: 0, lead_count: 0 },
    ])

  return (
    <div>
      <div className="mb-2 font-display text-xs font-medium text-black/50">Options</div>
      <SortableList
        items={options}
        onReorder={onChange}
        size="sm"
        renderItem={(opt, { dragHandle, moveControls }) => (
          <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-white p-3">
            <div className="flex items-center gap-0.5 pt-1">
              {dragHandle}
              {moveControls}
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <input
                type="text"
                value={opt.label}
                onChange={(e) => update(opt.id, { label: e.target.value })}
                placeholder="Label"
                className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 font-display text-sm font-medium text-ink focus:border-midnight-green focus:outline-none focus:ring-1 focus:ring-midnight-green"
              />
              <input
                type="text"
                value={opt.description}
                onChange={(e) => update(opt.id, { description: e.target.value })}
                placeholder="Description (optional)"
                className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 font-display text-xs text-black/70 focus:border-midnight-green focus:outline-none focus:ring-1 focus:ring-midnight-green"
              />
              {opt.lead_count > 0 && (
                <span className="font-display text-xs text-black/40">
                  {opt.lead_count} lead{opt.lead_count === 1 ? '' : 's'}
                </span>
              )}
            </div>
            <div className="flex flex-col items-center gap-1 pt-1">
              <IconPicker value={opt.icon} onChange={(icon) => update(opt.id, { icon })} />
              <input
                type="number"
                min={0}
                value={opt.score}
                onChange={(e) => update(opt.id, { score: parseInt(e.target.value) || 0 })}
                title="Score"
                className="w-12 rounded-md border border-gray-300 bg-white px-1 py-1 text-center font-display text-xs text-ink focus:border-midnight-green focus:outline-none focus:ring-1 focus:ring-midnight-green"
              />
              <button
                type="button"
                onClick={() => remove(opt.id)}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-400 hover:bg-rose-50 hover:text-rose-600"
                aria-label="Remove option"
              >
                <HiOutlineXMark className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      />
      <button
        type="button"
        onClick={add}
        className="mt-2 inline-flex items-center gap-1 rounded-md border border-dashed border-gray-300 bg-white px-3 py-1.5 font-display text-xs font-medium text-ink transition hover:bg-gray-50"
      >
        <HiOutlinePlus className="h-3.5 w-3.5" /> Add option
      </button>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────
// ContactFieldsEditor — sortable list of contact fields (in drawer)
// ────────────────────────────────────────────────────────────────

function ContactFieldsEditor({
  fields,
  onChange,
}: {
  fields: ContactFieldState[]
  onChange: (next: ContactFieldState[]) => void
}) {
  const update = (id: string, patch: Partial<ContactFieldState>) =>
    onChange(fields.map((f) => (f.id === id ? { ...f, ...patch } : f)))
  const remove = (id: string) => onChange(fields.filter((f) => f.id !== id))
  const add = () =>
    onChange([
      ...fields,
      {
        id: nextTempId(),
        db_id: null,
        key: '',
        label: '',
        field_type: 'text',
        required: false,
        system: false,
        locked_key: false,
        locked_field_type: false,
        locked_required: false,
        lead_count: 0,
      },
    ])

  return (
    <div>
      <div className="mb-2 font-display text-xs font-medium text-black/50">Contact fields</div>
      <SortableList
        items={fields}
        onReorder={onChange}
        size="sm"
        renderItem={(field, { dragHandle, moveControls }) => (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white p-3">
            <div className="flex items-center gap-0.5">
              {dragHandle}
              {moveControls}
            </div>
            <input
              type="text"
              value={field.label}
              onChange={(e) => update(field.id, { label: e.target.value })}
              placeholder="Label"
              className="min-w-0 flex-1 rounded-md border border-gray-300 bg-white px-2 py-1.5 font-display text-sm text-ink focus:border-midnight-green focus:outline-none focus:ring-1 focus:ring-midnight-green"
            />
            <input
              type="text"
              value={field.key}
              onChange={(e) => update(field.id, { key: e.target.value })}
              placeholder="key"
              disabled={field.locked_key}
              className="w-24 shrink-0 rounded-md border border-gray-300 bg-white px-2 py-1.5 font-display text-sm text-ink focus:border-midnight-green focus:outline-none focus:ring-1 focus:ring-midnight-green disabled:bg-gray-100 disabled:text-black/40"
            />
            <select
              value={field.field_type}
              onChange={(e) => update(field.id, { field_type: e.target.value as FieldType })}
              disabled={field.locked_field_type}
              className="rounded-md border border-gray-300 bg-white px-2 py-1.5 font-display text-sm text-ink focus:border-midnight-green focus:outline-none focus:ring-1 focus:ring-midnight-green disabled:bg-gray-100 disabled:text-black/40"
            >
              <option value="text">text</option>
              <option value="email">email</option>
              <option value="tel">tel</option>
              <option value="number">number</option>
              <option value="url">url</option>
            </select>
            <label className="inline-flex items-center gap-1 font-display text-xs text-black/60">
              <input
                type="checkbox"
                checked={field.required}
                disabled={field.locked_required}
                onChange={(e) => update(field.id, { required: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-[#93D500] focus:ring-[#93D500] disabled:opacity-50"
              />
              required
            </label>
            {field.system ? (
              <span className="whitespace-nowrap font-display text-xs text-black/40">system</span>
            ) : (
              <button
                type="button"
                onClick={() => remove(field.id)}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-400 hover:bg-rose-50 hover:text-rose-600"
                aria-label="Remove field"
              >
                <HiOutlineXMark className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      />
      <button
        type="button"
        onClick={add}
        className="mt-2 inline-flex items-center gap-1 rounded-md border border-dashed border-gray-300 bg-white px-3 py-1.5 font-display text-xs font-medium text-ink transition hover:bg-gray-50"
      >
        <HiOutlinePlus className="h-3.5 w-3.5" /> Add field
      </button>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────
// TemplatePickerDialog
// ────────────────────────────────────────────────────────────────

function TemplatePickerDialog({
  templates,
  onPick,
  onClose,
}: {
  templates: ServerTemplate[]
  onPick: (tpl: ServerTemplate) => void
  onClose: () => void
}) {
  const [q, setQ] = useState('')
  const filtered = useMemo(
    () =>
      templates.filter((t) =>
        `${t.text} ${t.description}`.toLowerCase().includes(q.trim().toLowerCase()),
      ),
    [templates, q],
  )

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-16 font-sans"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="w-full max-w-xl overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h2 className="font-display text-lg font-semibold text-midnight-green">
            Add step from template
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
          >
            <HiOutlineXMark className="h-5 w-5" />
          </button>
        </div>
        <div className="px-6 py-4">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search templates…"
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 font-display text-sm text-ink shadow-sm focus:border-midnight-green focus:outline-none focus:ring-1 focus:ring-midnight-green"
            autoFocus
          />
        </div>
        <div className="max-h-80 overflow-y-auto px-2 pb-4">
          {filtered.length === 0 ? (
            <p className="px-4 py-6 text-center font-display text-sm text-black/50">
              No templates match.
            </p>
          ) : (
            <ul className="flex flex-col gap-1">
              {filtered.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => onPick(t)}
                    className="flex w-full items-start justify-between gap-3 rounded-md px-4 py-3 text-left transition hover:bg-gray-50"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-display text-sm font-medium text-ink">{t.text}</p>
                      {t.description && (
                        <p className="mt-0.5 truncate font-display text-xs text-black/50">
                          {t.description}
                        </p>
                      )}
                      <p className="mt-1 font-display text-xs text-black/40">
                        {t.options.length} options · {t.question_type}
                      </p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────
// ImpactSummary — body content for the destructive-edit confirmation.
// ────────────────────────────────────────────────────────────────

function ImpactSummary({ impact }: { impact: DestructiveImpact }) {
  return (
    <div className="space-y-3">
      <p>
        Your changes will permanently affect{' '}
        <strong>
          {impact.total_leads_affected} lead{impact.total_leads_affected === 1 ? '' : 's'}
        </strong>
        :
      </p>
      {impact.deleted_steps.length > 0 && (
        <div>
          <p className="font-medium text-ink">Questions being deleted:</p>
          <ul className="mt-1 list-disc pl-5">
            {impact.deleted_steps.map((s, i) => (
              <li key={i}>
                "{s.text}" — {s.lead_count} answer{s.lead_count === 1 ? '' : 's'} lost
              </li>
            ))}
          </ul>
        </div>
      )}
      {impact.deleted_options.length > 0 && (
        <div>
          <p className="font-medium text-ink">Options being deleted:</p>
          <ul className="mt-1 list-disc pl-5">
            {impact.deleted_options.map((o, i) => (
              <li key={i}>
                "{o.label}" in "{o.step_text}" — {o.lead_count} answer
                {o.lead_count === 1 ? '' : 's'} lost
              </li>
            ))}
          </ul>
        </div>
      )}
      {impact.deleted_contact_fields.length > 0 && (
        <div>
          <p className="font-medium text-ink">Contact fields being deleted:</p>
          <ul className="mt-1 list-disc pl-5">
            {impact.deleted_contact_fields.map((f, i) => (
              <li key={i}>
                "{f.label}" — {f.lead_count} value{f.lead_count === 1 ? '' : 's'} lost
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="text-rose-600">This cannot be undone.</p>
    </div>
  )
}
